import * as Yup from 'yup'

import LookupType from './LookupType'

const GeneralComment = Yup.object().shape({
    id: Yup.number().nullable(),
    job_detail_id: Yup.number().nullable(),
    general_comment: Yup.string().nullable()
})

export const PebbleDetails = Yup.object().shape({
    id: Yup.number().nullable(),
    job_detail_id: Yup.number().nullable(),
    is_request_form: Yup.boolean().required(),
    prep_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    app_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    scheduled_app_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    detail_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    request_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    notes: Yup.string().nullable(),
    is_slurry_pickup: Yup.boolean().required(),
    total_batches: Yup.number().nullable().transform((v) => (v === '' || Number.isNaN(v) ? null : v)),
    slurry_pickup_notes: Yup.string().nullable(),
    is_can_show: Yup.boolean().required(),
    requested_app_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    is_complete_chip_out: Yup.boolean().required(),
    is_update_channel_drain: Yup.boolean().required(),
    is_trim_tile: Yup.boolean().required(),
    is_trash_pick_up: Yup.boolean().required(),

    tile_information: Yup.string().nullable().max(200, 'Must be 200 characters or less.'),
    prep_crew_id: Yup.number().nullable(),
    app_crew_id: Yup.number().nullable(),
    detail_crew_id: Yup.number().nullable(),
    is_no_prep_print: Yup.boolean().required(),

    prep_status: LookupType
        .label('Job Status')
        .default(null)
        .nullable(),
    app_status: LookupType
        .label('Job Status')
        .default(null)
        .nullable(),
    detail_status: LookupType
        .label('Job Status')
        .default(null)
        .nullable(),

    color_additions: Yup.array().of(Yup.object().shape({
        id: Yup.number(),
        color: LookupType.nullable(),
        material_type: LookupType.nullable(),
        job_detail_id: Yup.number(),
        pool_section: LookupType.nullable(),
        additions: Yup.array().of(LookupType),
        batches_used: Yup.string().nullable(),
        notes: Yup.string().nullable()
    })),

    crew_information: Yup.array().of(Yup.object().shape({
        id: Yup.number(),
        job_detail_id: Yup.number(),
        date: Yup.string().nullable(),
        crew: LookupType.nullable(),
        foreman: LookupType.nullable(),
        phase_type: LookupType.required('You must select a phase').typeError('You must select a phase'),
        job_order: LookupType.nullable(),
        type: Yup.string().nullable(),
        notes: Yup.string().nullable(),
        status: LookupType.nullable(),
        slurry_pickup: LookupType.nullable(),
    })).nullable()
})

export const GuniteHistoryEntry = Yup.object().shape({
    id: Yup.number().nullable(),
    job_detail_id: Yup.number().nullable(),
    status: LookupType.nullable(),
    crew: LookupType.nullable(),
    type: LookupType.test('is-valid-type', 'Required', (value) => value && value.id).typeError('Required'),
    yardage: Yup.string().nullable(),
    job_order: LookupType.nullable(),
    notes: Yup.string().nullable(),
    gunite_date: Yup.string().nullable().when(['type'], {
        is: (type) => type && type.id && type.id > 1 && type.id < 9,
        then: Yup.string().required('Required')
    })
})

export const GuniteDetails = Yup.object().shape({
    id: Yup.number().nullable(),
    job_detail_id: Yup.number().nullable(),
    request_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    requested_app_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    flash_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    finished_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    scheduled_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    total_yardage: Yup.string().nullable(),
    flash_yards: Yup.string().nullable(),
    notes: Yup.string().nullable(),
    crew_id: Yup.number().nullable(),
    is_spa_negative_edge: Yup.boolean().required(),
    is_spa_rolled_edge: Yup.boolean().required(),
    is_flash: Yup.boolean().required(),
    flash_crew_id: Yup.number().nullable(),

    gunite_history: Yup.array().of(GuniteHistoryEntry).nullable()
}).nullable()

export const BoulderDetails = Yup.array().of(Yup.object().shape({
    id: Yup.number().nullable(),
    job_detail_id: Yup.number().nullable(),
    status: LookupType.nullable(),
    addition: LookupType.nullable(),
    completed_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    notes: Yup.string().nullable(),
}))

export const WarrantyDetails = Yup.object().shape({
    id: Yup.number().nullable(),
    job_detail_id: Yup.number().nullable(),
    priority: LookupType.nullable(),
    description: LookupType.nullable(),
    description_notes: Yup.string().nullable(),
    special_instructions: Yup.string().nullable(),
    is_pebble_division: Yup.boolean(),
    is_gunite_division: Yup.boolean(),
    is_boulder_creations_division: Yup.boolean(),
    repair_supervisor: LookupType.nullable(),
    other_repair_supervisor: Yup.string().nullable(),
    category: LookupType.nullable(),
    ph: Yup.string().nullable(),
    cyanuric_acid: Yup.string().nullable(),
    sequestering_agent: Yup.string().nullable(),
    alkalinity: Yup.string().nullable(),
    chlorine: Yup.string().nullable(),
    calcium: Yup.string().nullable(),
    phosphates: Yup.string().nullable(),
    other_info: Yup.string().nullable(),
    report_notes: Yup.string().nullable(),
    contact_notes: Yup.string().nullable(),
    gunite_application_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    gunite_crew: LookupType.nullable(),
    pool_sections: Yup.array().of(LookupType),
    pool_info: Yup.string().nullable(),
    is_will_need_to_return: Yup.boolean().required(),
    job_entered_by: Yup.string().nullable(),
    invoice_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    creation_date: Yup.string().nullable(),

    schedule: Yup.array().of(Yup.object().shape({
        id: Yup.number().nullable(),
        job_detail_id: Yup.number(),
        fieldworker: LookupType.nullable(),
        start: Yup.string().nullable(),
        end: Yup.string().nullable(),
        completion: Yup.string().nullable(),
        additional_notes: Yup.string().nullable()
    })),

    pebble_details: Yup.array().of(Yup.object().shape({
        id: Yup.number().nullable(),
        job_detail_id: Yup.number(),
        app_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
        detail_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
        color: Yup.string().nullable(),
        additions: Yup.string().nullable(),
        section: Yup.string().nullable(),
        crew: Yup.string().nullable(),
        detail_crew: Yup.string().nullable()
    })),

    crew_repair_jobs: Yup.array().of(Yup.object().shape({
        id:Yup.number().nullable(),
        job_detail_id: Yup.number(),
        date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
        company_division: LookupType.required('Department is required.').typeError('Department is required.'),
        crew: LookupType.nullable(),
        status: LookupType.nullable(),
        notes: Yup.string().nullable(),
        pebble_phase_type: LookupType.nullable()
    }))
}).nullable()


const PresiteDetailComment = Yup.object().shape({
    id: Yup.number().nullable(),
    job_detail_id: Yup.number().nullable(),
    date_created: Yup.string().nullable(),
    comment: Yup.string().nullable()
})

export const Presite = Yup.object().shape({
    id: Yup.string().nullable(),
    job_detail_id: Yup.string().nullable(),
    checkin_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable(),
    slurry_type: LookupType.nullable(),
    slurry_type_other: Yup.string().nullable(),
    slurry_type_notes: Yup.string().nullable(),
    rope_anchors: Yup.number().min(0).label('Rope Anchors').nullable(),
    umbrella_sleeves: Yup.number().min(0).label('Umbrella Sleeeves').nullable(),
    vgb_main_drains: Yup.string().label('VGB Main Drains').nullable(),
    vgb_main_drains_by: LookupType.nullable(),
    fittings_pool: Yup.number().min(0).label('Pool Fittings').nullable(),
    fittings_spa: Yup.string().label('Spa Fittings').nullable(),
    fittings_other: Yup.number().min(0).label('Other Fittings').nullable(),
    eyeballs_pool: Yup.number().min(0).label('Pool Eyeballs').nullable(),
    eyeballs_spa: Yup.number().min(0).label('Spa Eyeballs').nullable(),
    eyeballs_slotted: Yup.number().min(0).label('Slotted Eyeballs').nullable(),
    in_floor_nozzles: Yup.number().min(0).label('In-floor Nozzles').nullable(),
    estimated_number_of_batches: Yup.number().min(0).label('Estimated Number of Batches').nullable(),
    estimated_number_of_hoses: Yup.number().min(0).label('Estimated Number of Hoses').nullable(),
    install_trim_tile_mosaics: Yup.boolean(),
    is_bond_cote: Yup.boolean(),
    is_complete_chip_out: Yup.boolean(),
    is_weeping: Yup.boolean(),
    comments: Yup.array().of(PresiteDetailComment).nullable(),

    //gunite fields
    is_water_on_site: Yup.boolean(),
    is_access_to_power: Yup.boolean(),
    feet_of_hose_needed: Yup.number().min(0).label('Feet of Hose Needed').nullable(),
    is_pool_ready_to_shoot: Yup.boolean(),
    access_notes: Yup.string().label('Access Notes').nullable(),
    is_job_caved_in: Yup.boolean(),
    is_needs_inspection: Yup.boolean(),
    is_steel_done: Yup.boolean(),
    steel_notes: Yup.string().label('Steel Notes').nullable(),
    is_stub_out: Yup.boolean(),
    is_depth_matches_plan: Yup.boolean(),
    additional_depth_notes: Yup.string().label('Additional Depth Notes').nullable(),
    plan_change_notes: Yup.string().label('Plan Change Notes').nullable(),

    //gunite and pebble
    scheduled_date: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date.").nullable().label('Scheduled Date'),
    presite_supervisor: LookupType.nullable()
}).nullable() //nullable since Warranty records should not have Presite data

export const PresiteAlone = Yup.object().shape({
    presite: Presite
})

export const JobDetailSchema = Yup.object().shape({
    id: Yup.number().nullable(),
    job_types: Yup.array().of(LookupType).nullable(),
    job_category: LookupType.nullable(),
    status: LookupType
        .label('Job Status')
        .default(null)
        .nullable(),
    plan_perimeter: Yup.string().nullable(),
    pebble_perimeter: Yup.string().nullable(),
    mmg_perimeter: Yup.string().nullable(),
    mmg_pebble_perimeter: Yup.string().nullable(),
    plan_sa: Yup.string().nullable(),
    plan_ia: Yup.string().nullable(),
    mmg_ia: Yup.string().nullable(),
    quoted_ia: Yup.string().nullable(),
    depth_low: Yup.string().nullable(),
    depth_high: Yup.string().nullable(),
    depth_mid: Yup.string().nullable(),
    is_cash_on_delivery: Yup.boolean().required(),
    is_in_print_queue: Yup.boolean(),
    is_critical_note: Yup.boolean().required(),
    critical_note: Yup.string().nullable(),
    awaiting_builder_date: Yup.string().nullable(),
    is_invoiced: Yup.boolean(),
    
    pool_type: LookupType
        .label('Pool Type')
        .default(null)
        .nullable(),

    spa_type: LookupType
        .label('Spa Type')
        .default(null)
        .nullable(),

    general_comments: Yup.array().of(GeneralComment).nullable(),

    pebble_details: PebbleDetails.nullable(),
    gunite_details: GuniteDetails,
    boulder_details: BoulderDetails,
    warranty_details: WarrantyDetails,
    presite: Presite
})

export const JobSchema = Yup.object().shape({
    company: LookupType
        .label('Company')
        .default(null)
        .nullable()
        .required(),

    company_supervisor: LookupType
        .label('Supervisor')
        .default(null)
        .nullable(),

    name: Yup
        .string()
        .label('Job Name')
        .min(1)
        .required(),

    phone: Yup
        .string()
        .label('Phone')
        .nullable()
        .length(14, 'Please enter a 10-digit phone number.'),

    key_map: Yup.string().nullable(),
    entered_date: Yup.string().nullable(),
    lat: Yup.number().nullable(),
    lon: Yup.number().nullable(),
    address_line_1: Yup.string().nullable(),
    address_line_2: Yup.string().nullable(),
    city: Yup.string().nullable(),
    state: Yup.string().nullable(),
    zip_code: Yup.string().nullable(),
    county: Yup.string().nullable(),
    mmg_mileage: Yup.string().nullable(),
    manchaca_mileage: Yup.string().nullable(),
    gate_code: Yup.string().nullable(),
    notes: Yup.string().nullable(),
    is_central_texas: Yup.boolean().required(),
    is_display: Yup.boolean().required(),
    is_commercial: Yup.boolean().required(),

    homeowner_first_name: Yup.string().nullable(),
    homeowner_last_name: Yup.string().nullable(),
    homeowner_phone: Yup
        .string()
        .label('Homeowner Phone')
        .nullable()
        .length(14, 'Please enter a 10-digit phone number.'),
    homeowner_secondary_phone: Yup
        .string()
        .label('Homeowner Secondary Phone')
        .nullable()
        .length(14, 'Please enter a 10-digit phone number.'),
    homeowner_email: Yup.string().nullable(),
    was_estimated: Yup.boolean(),
    estimate_to_merge: LookupType,

    temp_lat: Yup.number().nullable(),
    temp_lon: Yup.number().nullable(),

    sub_jobs: Yup.array().of(JobDetailSchema)
})
